<template>
    <div class="flex py-4 text-sm justify-between">
      <div class="w-32">
        <Superselect
          v-model="perPage"
          title="Per Page"
          :multiple="false"
          :options="perPageOptions"
          @input="pageSelection"
          style="min-width:4.6rem; border: none;"
        ></Superselect>
      </div>
      <div class="block flex items-center justify-center gap-2">
        <button
          @click="goToFirstPage"
          :disabled="data.currentPage === 1"
          class="page-button !text-xs"
        >
          <v-icon name="md-firstpage-round" width="1em" height="1em" />
        </button>
  
        <button
          @click="previousPage"
          :disabled="data.currentPage === 1"
            class="page-button !text-xs"
        >
          <v-icon name="md-navigatebefore-round" width="1em" height="1em"/>
        </button>
  
        <div :key="data.total">
          <div v-if="lastPage >= 5" class="flex gap-1">
            <template v-for="n in windowSize" :key="n">
              <button
                @click="loadPage(windowStart + n - 1)"
                v-html="windowStart + n - 1"
                class="page-button"
                :class="{ highlight: isCurrentPage(windowStart + n - 1) }"
              ></button>
            </template>
          </div>
          <div v-else class="flex gap-1">
            <template v-for="page in lastPage" :key="page">
              <button
                @click="loadPage(page)"
                v-html="page"
                class="page-button"
                :class="{ highlight: isCurrentPage(page) }"
              ></button>
            </template>
          </div>
        </div>
  
        <button
          @click="nextPage"
          :disabled="data.currentPage === lastPage"
          class="page-button !text-xs"
        >
          <v-icon name="md-navigatenext-round" width="1em" height="1em" />
        </button>
  
        <button
          @click="goToLastPage"
          :disabled="data.currentPage === lastPage"
          class="page-button !text-xs"
        >
          <v-icon name="md-lastpage-round" width="1em" height="1em" />
        </button>
      </div>
    </div>
  </template>
  
  <script setup>
  import { computed, ref } from "vue";
  import Superselect from '@/components/common/Superselect.vue'
  import XButton from '@/components/common/XButton.vue'

  // Props
  const props = defineProps({
    data: {
      type: Object,
      required: true,
    },
    onEachSide: {
      type: Number,
      default: 3,
    },
  });

  const emit = defineEmits(['load-page', 'next-page', 'previous-page', 'per-page']);
  
  // Reactive references
  const perPage = ref(props.data.perPage);
  const perPageOptions = [10, 20, 50, 100];
  
  // Computed properties
  const lastPage = computed(() => props.data.lastPage);
  
  const windowSize = computed(() =>
    Math.min(lastPage.value, props.onEachSide * 2 + 1)
  );
  
  const windowStart = computed(() => {
    if (!props.data || props.data.currentPage <= props.onEachSide) {
      return 1;
    } else if (props.data.currentPage >= lastPage.value - props.onEachSide) {
      return Math.max(1, lastPage.value - windowSize.value + 1);
    } else {
      const diff = windowSize.value - (lastPage.value - props.data.currentPage);
      return Math.max(
        1,
        props.data.currentPage - props.onEachSide - Math.max(0, diff)
      );
    }
  });
  
  // Methods
  const isCurrentPage = (page) => page === props.data.currentPage;
  
  const loadPage = (page) => {
    emit("load-page", page);
  };
  
  const nextPage = () => {
    if (props.data.currentPage < props.data.lastPage) {
      emit("next-page");
    }
  };
  
  const previousPage = () => {
    if (props.data.currentPage > 1) {
      emit("previous-page");
    }
  };
  
  const goToFirstPage = () => {
    emit("load-page", 1);
  };
  
  const goToLastPage = () => {
    emit("load-page", props.data.lastPage);
  };
  
  const pageSelection = (value) => {
    emit("per-page", value);
  };
  </script>
  
  <style scoped>
  button.page-button{
    @apply px-3 hover:bg-base-content/5 rounded-md p-2 text-base-content;
  }

  button.page-button:disabled {
    @apply opacity-25 hover:bg-transparent;
  }

  button.highlight {
    @apply bg-base-content/10;
  }
  </style>